import React, { useCallback, useMemo } from "react";

import Menu from "antd/es/menu";
import cn from "classnames";
import { FormattedMessage } from "react-intl";

import Activity from "@mapmycustomers/shared/types/entity/Activity";
import { LoadingSpinner } from "@mapmycustomers/ui";

import { RecurringActionType } from "@app/component/createEditEntity/Activity/enum/RecurringActionType";
import useAnalytics from "@app/util/contexts/useAnalytics";
import isEmailActivity from "@app/util/email/isEmailActivity";

import styles from "./ActivityMenu.module.scss";

interface Props {
  activity: Activity;
  deleteActivity: (payload: {
    deleteType: RecurringActionType | undefined;
    id: Activity["id"];
  }) => void;
  deleteLoadingIds: Activity["id"][];
  extraActions?: React.ReactNode[];
  openDuplicationModal: (id: Activity["id"]) => void;
}

const ActivityMenu: React.FC<Props> = ({
  activity,
  deleteActivity,
  deleteLoadingIds,
  extraActions,
  openDuplicationModal,
}) => {
  const analyticIssuer = useAnalytics();

  const isDeleting = useMemo(
    () => deleteLoadingIds.includes(activity.id),
    [activity, deleteLoadingIds]
  );

  const handleDuplicateClick = useCallback(() => {
    openDuplicationModal(activity.id);
    analyticIssuer?.clicked(["Duplicate Activity in Table Row"], {
      duplicatingActivity: activity.name,
    });
  }, [activity, analyticIssuer, openDuplicationModal]);

  const handleDeleteClick = useCallback(
    (deleteType: RecurringActionType | undefined) => {
      deleteActivity({ id: activity.id, deleteType });
      analyticIssuer?.clicked(["Delete Activity Complete in Table Row"], {
        duplicatingActivity: activity.name,
      });
    },
    [activity.id, activity.name, analyticIssuer, deleteActivity]
  );

  return (
    <Menu className={cn(styles.menu, "activity-menu")} selectedKeys={[]}>
      {isDeleting ? (
        <Menu.Item>
          <LoadingSpinner mini />
        </Menu.Item>
      ) : (
        <>
          {(extraActions ?? []).map((node) => node)}
          <Menu.Item disabled={isEmailActivity(activity)} onClick={handleDuplicateClick}>
            <FormattedMessage
              id="activity.column.actions.duplicateActivity"
              defaultMessage="Duplicate activity"
              description="Label for duplicate activity action button"
            />
          </Menu.Item>
          {activity.recurInterval ? (
            <Menu.SubMenu
              title={
                <FormattedMessage
                  id="activity.column.actions.deleteRepeatingActivity"
                  defaultMessage="Delete repeating activity"
                  description="Label for delete repeating activity action button"
                />
              }
            >
              <Menu.Item onClick={() => handleDeleteClick(undefined)}>
                <FormattedMessage
                  id="activity.column.actions.deleteRepeatingActivity.onlyThis"
                  defaultMessage="Only this activity"
                  description="Label for delete only this activity action button"
                />
              </Menu.Item>
              <Menu.Item onClick={() => handleDeleteClick(RecurringActionType.Future)}>
                <FormattedMessage
                  id="activity.column.actions.deleteRepeatingActivity.thisAndFuture"
                  defaultMessage="This and future activities"
                  description="Label for delete this and future activities action button"
                />
              </Menu.Item>
              <Menu.Item onClick={() => handleDeleteClick(RecurringActionType.All)}>
                <FormattedMessage
                  id="activity.column.actions.deleteRepeatingActivity.all"
                  defaultMessage="All activities (past and future)"
                  description="Label for delete past and future activities action button"
                />
              </Menu.Item>
            </Menu.SubMenu>
          ) : (
            <Menu.Item onClick={() => handleDeleteClick(undefined)}>
              <FormattedMessage
                id="activity.column.actions.deleteActivity"
                defaultMessage="Delete activity"
                description="Label for delete activity action button"
              />
            </Menu.Item>
          )}
        </>
      )}
    </Menu>
  );
};

export default ActivityMenu;
